import React, { useState, useMemo } from 'react';
import {
  DollarSign, Printer, Users, FileText, Settings2,
  ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetSessionsQuery,
  useGetEstimationCopiesQuery,
  useGetBudgetReelQuery,
} from '../../services/emploisTempsApi';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FormateurBudget {
  enseignant_id: number;
  nom: string;
  matieres: {
    nom: string;
    niveau: string;
    groupe: string;
    classes: string[];
    copies: number;
    effectif: number;
  }[];
  total_copies: number;
  total_effectif: number;
  montant: number;
  bonus: number; // bonus pour matières < 10 copies
  montant_total: number;
}

// ─── Constantes ─────────────────────────────────────────────────────────────

const PRIX_COPIE = 100; // FCFA par copie
const SEUIL_BONUS = 10; // Si total copies du formateur < 10, bonus appliqué
const BONUS_MONTANT = 1000; // FCFA de bonus unique si total copies < seuil

const FONTS = [
  'Times New Roman', 'Calibri', 'Arial', 'Georgia', 'Trebuchet MS', 'Verdana',
];

// ─── Formatage FCFA ─────────────────────────────────────────────────────────

const fmtFCFA = (n: number): string => {
  return n.toLocaleString('fr-FR') + ' FCFA';
};

// ─── Composant principal ────────────────────────────────────────────────────

type BudgetMode = 'estimatif' | 'reel';

const BudgetCorrection: React.FC = () => {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [marge, setMarge] = useState(15);
  const [budgetMode, setBudgetMode] = useState<BudgetMode>('estimatif');
  const [fontFamily, setFontFamily] = useState('Times New Roman');
  const [fontSize, setFontSize] = useState(100);
  const [chefScolarite, setChefScolarite] = useState('Donald Christ MBEMBA LOKO');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data: sessions = [] } = useGetSessionsQuery({});
  const { data: estimation, isLoading } = useGetEstimationCopiesQuery(
    { sessionId: sessionId!, marge },
    { skip: !sessionId }
  );
  const { data: budgetReelData, isLoading: loadingReel } = useGetBudgetReelQuery(
    sessionId!,
    { skip: !sessionId }
  );

  // Budget réel par formateur (directement depuis l'API)
  const budgetFormateursReel = useMemo<FormateurBudget[]>(() => {
    if (!budgetReelData?.formateurs) return [];
    return budgetReelData.formateurs.map(f => ({
      enseignant_id: f.enseignant_id,
      nom: f.nom,
      matieres: f.matieres.map(m => ({
        nom: m.nom,
        niveau: m.niveau,
        groupe: '',
        classes: m.classes ?? [],
        copies: m.copies ?? m.copies_estim ?? 0,
        effectif: m.copies ?? m.copies_estim ?? 0,
      })),
      total_copies: f.total_copies,
      total_effectif: f.total_copies,
      montant: f.montant,
      bonus: f.bonus,
      montant_total: f.montant_total,
    }));
  }, [budgetReelData]);

  const statsReel = useMemo(() => {
    if (!budgetReelData) return null;
    return {
      totalCopies: budgetReelData.total_copies,
      totalMontant: budgetReelData.total_montant,
      totalBonus: budgetReelData.total_bonus,
      nbFormateurs: budgetReelData.nb_formateurs,
    };
  }, [budgetReelData]);

  // Agréger par formateur (estimatif)
  const budgetFormateurs = useMemo<FormateurBudget[]>(() => {
    if (!estimation?.details) return [];

    const map = new Map<number, FormateurBudget>();

    for (const detail of estimation.details) {
      const formateurs = detail.formateurs || [];
      if (formateurs.length === 0) continue;

      // Répartir les copies entre formateurs (si plusieurs)
      const copiesParFormateur = Math.ceil(detail.copies_avec_marge / formateurs.length);
      const effectifParFormateur = Math.ceil(detail.effectif / formateurs.length);

      for (const fmt of formateurs) {
        if (!map.has(fmt.enseignant_id)) {
          map.set(fmt.enseignant_id, {
            enseignant_id: fmt.enseignant_id,
            nom: fmt.nom,
            matieres: [],
            total_copies: 0,
            total_effectif: 0,
            montant: 0,
            bonus: 0,
            montant_total: 0,
          });
        }

        const fb = map.get(fmt.enseignant_id)!;

        // Extraire les classes depuis le groupe (ex: "BTP-AU-TG" → ["L2BTP", "L2AU", "L2TG"])
        const classesDuGroupe = detail.groupe
          ? detail.groupe.split('-').map((g: string) => `${detail.niveau}${g.trim()}`)
          : [detail.niveau];

        // Vérifier si cette matière est déjà ajoutée (même nom + niveau)
        const existing = fb.matieres.find(m => m.nom === detail.matiere && m.niveau === detail.niveau);
        if (existing) {
          existing.copies += copiesParFormateur;
          existing.effectif += effectifParFormateur;
          // Fusionner les classes sans doublons
          classesDuGroupe.forEach((c: string) => {
            if (!existing.classes.includes(c)) existing.classes.push(c);
          });
        } else {
          fb.matieres.push({
            nom: detail.matiere,
            niveau: detail.niveau,
            groupe: detail.groupe,
            classes: [...classesDuGroupe],
            copies: copiesParFormateur,
            effectif: effectifParFormateur,
          });
        }
      }
    }

    // Calculer les montants
    const result: FormateurBudget[] = [];
    const allFormateurs = Array.from(map.values());
    for (const fb of allFormateurs) {
      fb.total_copies = fb.matieres.reduce((s: number, m: { copies: number }) => s + m.copies, 0);
      fb.total_effectif = fb.matieres.reduce((s: number, m: { effectif: number }) => s + m.effectif, 0);
      fb.montant = fb.total_copies * PRIX_COPIE;

      // Bonus : si la somme de TOUTES les copies du formateur est < SEUIL_BONUS → 1 seul bonus
      fb.bonus = fb.total_copies < SEUIL_BONUS ? BONUS_MONTANT : 0;
      fb.montant_total = fb.montant + fb.bonus;

      result.push(fb);
    }

    // Trier par nom
    return result.sort((a, b) => a.nom.localeCompare(b.nom));
  }, [estimation]);

  // Stats globales
  const stats = useMemo(() => {
    const totalCopies = budgetFormateurs.reduce((s, f) => s + f.total_copies, 0);
    const totalMontant = budgetFormateurs.reduce((s, f) => s + f.montant_total, 0);
    const totalBonus = budgetFormateurs.reduce((s, f) => s + f.bonus, 0);
    return { totalCopies, totalMontant, totalBonus, nbFormateurs: budgetFormateurs.length };
  }, [budgetFormateurs]);

  // Formateurs et stats actifs selon le mode
  const activeBudget = budgetMode === 'reel' ? budgetFormateursReel : budgetFormateurs;
  const activeStats = budgetMode === 'reel' && statsReel ? statsReel : stats;

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Impression ──
  const handlePrint = () => {
    if (activeBudget.length === 0) return;

    const session = sessions.find(s => s.id === sessionId);
    const s = (pt: number) => `${(pt * fontSize / 100).toFixed(1)}pt`;
    const NAVY = '#1A2E4A';

    let rows = '';
    activeBudget.forEach((fb, idx) => {
      const matList = fb.matieres.map(m => `${m.nom} (${m.classes.join(', ')})`).join(', ');
      const hasBonusMarker = fb.bonus > 0 ? ' *' : '';
      rows += `<tr style="background:${idx % 2 === 0 ? '#fff' : '#f8f9fa'};">
        <td style="border:1px solid #999;padding:4px 8px;text-align:center;">${idx + 1}</td>
        <td style="border:1px solid #999;padding:4px 8px;font-weight:bold;">${fb.nom}</td>
        <td style="border:1px solid #999;padding:4px 8px;font-size:${s(7)};line-height:1.3;">${matList}</td>
        <td style="border:1px solid #999;padding:4px 8px;text-align:center;">${fb.matieres.length}</td>
        <td style="border:1px solid #999;padding:4px 8px;text-align:center;font-weight:bold;">${fb.total_copies}</td>
        <td style="border:1px solid #999;padding:4px 8px;text-align:right;">${fmtFCFA(fb.montant)}</td>
        <td style="border:1px solid #999;padding:4px 8px;text-align:right;color:#B45309;">${fb.bonus > 0 ? fmtFCFA(fb.bonus) : '—'}</td>
        <td style="border:1px solid #999;padding:4px 8px;text-align:right;font-weight:bold;color:${NAVY};">${fmtFCFA(fb.montant_total)}${hasBonusMarker}</td>
        <td style="border:1px solid #999;padding:4px 8px;width:90px;"></td>
      </tr>`;
    });

    // Total row
    rows += `<tr style="background:#E8E8E8;font-weight:bold;">
      <td colspan="4" style="border:1px solid #999;padding:6px 8px;text-align:right;">TOTAL GÉNÉRAL</td>
      <td style="border:1px solid #999;padding:6px 8px;text-align:center;">${activeStats.totalCopies}</td>
      <td style="border:1px solid #999;padding:6px 8px;text-align:right;">${fmtFCFA(activeStats.totalMontant - activeStats.totalBonus)}</td>
      <td style="border:1px solid #999;padding:6px 8px;text-align:right;color:#B45309;">${fmtFCFA(activeStats.totalBonus)}</td>
      <td style="border:1px solid #999;padding:6px 8px;text-align:right;color:${NAVY};font-size:${s(10)};">${fmtFCFA(activeStats.totalMontant)}</td>
      <td style="border:1px solid #999;padding:6px 8px;"></td>
    </tr>`;

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Budget Correction — ${session?.nom || ''}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  @media print { .no-print { display: none !important; } }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: '${fontFamily}', serif; font-size: ${s(9)}; color: #1a1a1a; background: #fff; padding: 10px; }
  table { width: 100%; border-collapse: collapse; }
</style></head><body>

<div style="display:flex;align-items:center;justify-content:center;gap:16px;margin-bottom:8px;">
  <img src="/logo.png" alt="Logo" style="height:55px;" onerror="this.style.display='none'" />
  <div style="text-align:center;">
    <div style="font-size:${s(13)};font-weight:bold;letter-spacing:1px;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:${s(8)};font-style:italic;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
  </div>
</div>

<div style="text-align:center;margin:10px 0;padding:8px;background:${NAVY};color:#FFD700;font-size:${s(13)};font-weight:bold;letter-spacing:2px;border-radius:4px;">
  BUDGET ${budgetMode === 'reel' ? 'RÉEL' : 'ESTIMATIF'} DE CORRECTION DES COPIES D'EXAMEN
</div>

<div style="display:flex;justify-content:space-between;margin:8px 0;font-size:${s(9)};">
  <div>
    <strong>Session :</strong> ${session?.nom || '—'} &nbsp;&nbsp;|&nbsp;&nbsp;
    <strong>Année :</strong> ${estimation?.annee_academique || '—'} &nbsp;&nbsp;|&nbsp;&nbsp;
    <strong>Semestre :</strong> ${estimation?.semestre || '—'}
  </div>
  <div>
    <strong>Marge :</strong> +${marge}% &nbsp;&nbsp;|&nbsp;&nbsp;
    <strong>Prix/copie :</strong> ${PRIX_COPIE} FCFA &nbsp;&nbsp;|&nbsp;&nbsp;
    <strong>Bonus (&lt;${SEUIL_BONUS} copies) :</strong> +${fmtFCFA(BONUS_MONTANT)}
  </div>
</div>

<table style="margin-top:6px;">
  <thead>
    <tr style="background:${NAVY};color:#FFD700;">
      <th style="border:1px solid #999;padding:5px 6px;width:30px;">N°</th>
      <th style="border:1px solid #999;padding:5px 6px;">Formateur</th>
      <th style="border:1px solid #999;padding:5px 6px;">Matières</th>
      <th style="border:1px solid #999;padding:5px 6px;width:45px;">Nb Mat.</th>
      <th style="border:1px solid #999;padding:5px 6px;width:55px;">Copies</th>
      <th style="border:1px solid #999;padding:5px 6px;width:90px;">Montant</th>
      <th style="border:1px solid #999;padding:5px 6px;width:70px;">Bonus</th>
      <th style="border:1px solid #999;padding:5px 6px;width:100px;">Total</th>
      <th style="border:1px solid #999;padding:5px 6px;width:90px;">Émargement</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>

<div style="font-size:${s(7.5)};color:#666;margin-top:6px;">
  * Matières avec moins de ${SEUIL_BONUS} copies : bonus de ${fmtFCFA(BONUS_MONTANT)} par matière concernée.
</div>

<div style="margin-top:30px;display:flex;justify-content:space-between;align-items:flex-end;">
  <div style="font-size:${s(9)};">
    <div>Fait à Pointe-Noire, le ${today}</div>
  </div>
  <div style="text-align:center;">
    <div style="font-size:${s(9)};font-weight:bold;">Le Chef de Service de la Scolarité et des Examens</div>
    <div style="margin-top:40px;border-top:1px solid #000;padding-top:4px;min-width:250px;">
      <strong>${chefScolarite}</strong>
    </div>
  </div>
</div>

<div style="margin-top:15px;text-align:center;font-size:${s(7)};color:#999;border-top:1px solid #ddd;padding-top:4px;">
  Institut International 2i — Budget de correction — Document généré le ${today}
</div>

</body></html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <DollarSign size={28} className="text-green-600" />
            Budget de Correction
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
              budgetMode === 'reel'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
            }`}>
              {budgetMode === 'reel' ? 'RÉEL' : 'ESTIMATIF'}
            </span>
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            {budgetMode === 'reel'
              ? 'Budget basé sur les copies effectivement associées aux formateurs'
              : 'Budget estimatif basé sur les effectifs inscrits + marge de sécurité'}
          </p>
          {/* Grille tarifaire active (fix 2026-08-31) — visible pour éviter
              tout malentendu entre PN (100/copie + bonus) et BZ (forfait). */}
          {budgetReelData?.grille && (
            <div className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-[11px] font-medium ${
              budgetReelData.grille.deployment === 'bz'
                ? 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                : 'bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800'
            }`}>
              <span className="font-bold uppercase">{budgetReelData.grille.deployment}</span>
              <span>·</span>
              <span>{budgetReelData.grille.label}</span>
            </div>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          icon={<Printer size={16} />}
          onClick={handlePrint}
          disabled={budgetFormateurs.length === 0}
        >
          Imprimer le budget
        </Button>
      </div>

      {/* Paramètres */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Session */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Session d'examen *</label>
            <select
              value={sessionId ?? ''}
              onChange={e => setSessionId(e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">-- Sélectionner --</option>
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nom} — {s.annee_academique} S{s.semestre}
                </option>
              ))}
            </select>
          </div>

          {/* Mode budget */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type de budget</label>
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <button
                onClick={() => setBudgetMode('estimatif')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all ${
                  budgetMode === 'estimatif'
                    ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-300 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Estimatif
              </button>
              <button
                onClick={() => setBudgetMode('reel')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all ${
                  budgetMode === 'reel'
                    ? 'bg-white dark:bg-gray-600 text-green-700 dark:text-green-300 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Réel
              </button>
            </div>
          </div>

          {/* Marge (estimatif uniquement) */}
          <div className={budgetMode === 'reel' ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Marge de sécurité</label>
            <select
              value={marge}
              onChange={e => setMarge(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {[0, 5, 10, 15, 20, 25, 30].map(v => (
                <option key={v} value={v}>{v === 0 ? '0% (sans marge)' : `+${v}%`}</option>
              ))}
            </select>
          </div>

          {/* Police */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Police d'impression</label>
            <select
              value={fontFamily}
              onChange={e => setFontFamily(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {FONTS.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          {/* Taille */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Taille ({fontSize}%)</label>
            <input
              type="range" min={70} max={140} step={5}
              value={fontSize}
              onChange={e => setFontSize(Number(e.target.value))}
              className="w-full mt-2"
            />
          </div>
        </div>

        {/* Chef scolarité */}
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Chef de Service Scolarité (signature)</label>
          <input
            type="text"
            value={chefScolarite}
            onChange={e => setChefScolarite(e.target.value)}
            className="w-full md:w-96 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </Card>

      {/* Comparaison Estimatif vs Réel */}
      {(budgetFormateurs.length > 0 || budgetFormateursReel.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Carte Estimatif */}
          <div className={`rounded-xl border-2 p-5 transition-all cursor-pointer ${
            budgetMode === 'estimatif'
              ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10 shadow-lg'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300'
          }`} onClick={() => setBudgetMode('estimatif')}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-600 text-white text-[10px] font-bold rounded uppercase">Estimatif</span>
                <span className="text-xs text-gray-500">Basé sur les inscrits + marge {marge}%</span>
              </div>
              {budgetMode === 'estimatif' && <span className="text-blue-600 text-xs font-bold">● Actif</span>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Formateurs</p>
                <p className="text-xl font-bold text-blue-600">{stats.nbFormateurs}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Copies</p>
                <p className="text-xl font-bold text-purple-600">{stats.totalCopies.toLocaleString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Budget total</p>
                <p className="text-xl font-bold text-blue-700">{fmtFCFA(stats.totalMontant)}</p>
              </div>
            </div>
          </div>

          {/* Carte Réel */}
          <div className={`rounded-xl border-2 p-5 transition-all cursor-pointer ${
            budgetMode === 'reel'
              ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10 shadow-lg'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300'
          }`} onClick={() => setBudgetMode('reel')}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-600 text-white text-[10px] font-bold rounded uppercase">Réel</span>
                <span className="text-xs text-gray-500">Basé sur les copies associées</span>
              </div>
              {budgetMode === 'reel' && <span className="text-green-600 text-xs font-bold">● Actif</span>}
            </div>
            {statsReel ? (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Formateurs</p>
                  <p className="text-xl font-bold text-green-600">{statsReel.nbFormateurs}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Copies</p>
                  <p className="text-xl font-bold text-purple-600">{statsReel.totalCopies.toLocaleString('fr-FR')}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Budget total</p>
                  <p className="text-xl font-bold text-green-700">{fmtFCFA(statsReel.totalMontant)}</p>
                </div>
              </div>
            ) : loadingReel ? (
              <div className="text-center py-3 text-gray-400 text-sm">Chargement...</div>
            ) : (
              <div className="text-center py-3 text-gray-400 text-sm">Aucune copie associée</div>
            )}
            {statsReel && stats.totalMontant > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 flex justify-between">
                <span>Écart estimatif → réel</span>
                <span className={`font-bold ${statsReel.totalMontant <= stats.totalMontant ? 'text-green-600' : 'text-red-600'}`}>
                  {statsReel.totalMontant <= stats.totalMontant ? '−' : '+'}{fmtFCFA(Math.abs(statsReel.totalMontant - stats.totalMontant))}
                  {' '}({Math.round((statsReel.totalMontant - stats.totalMontant) / stats.totalMontant * 100)}%)
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats détaillées du mode actif */}
      {activeBudget.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Formateurs</p>
                <p className="text-2xl font-bold text-blue-600">{activeStats.nbFormateurs}</p>
              </div>
              <Users size={24} className="text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Copies</p>
                <p className="text-2xl font-bold text-purple-600">{activeStats.totalCopies.toLocaleString('fr-FR')}</p>
              </div>
              <FileText size={24} className="text-purple-400 opacity-50" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Bonus ({`<${SEUIL_BONUS} copies`})</p>
                <p className="text-2xl font-bold text-amber-600">{fmtFCFA(activeStats.totalBonus)}</p>
              </div>
              <Settings2 size={24} className="text-amber-400 opacity-50" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-green-300 dark:border-green-700 p-4 bg-green-50 dark:bg-green-900/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">BUDGET TOTAL</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{fmtFCFA(activeStats.totalMontant)}</p>
              </div>
              <DollarSign size={28} className="text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}

      {/* Pas de session */}
      {!sessionId && !isLoading && (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <DollarSign size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium text-gray-500">Sélectionnez une session d'examen</p>
            <p className="text-sm mt-1">Le budget sera calculé automatiquement</p>
          </div>
        </Card>
      )}

      {/* Aucun formateur */}
      {sessionId && !isLoading && activeBudget.length === 0 && (estimation || budgetReelData) && (
        <Card>
          <div className="text-center py-12">
            <AlertTriangle size={40} className="mx-auto mb-3 text-amber-500" />
            <p className="font-medium text-gray-600 dark:text-gray-300">Aucun formateur assigné aux matières</p>
            <p className="text-sm text-gray-400 mt-1">Assignez des formateurs dans la Gestion des Matières pour voir le budget</p>
          </div>
        </Card>
      )}

      {/* Tableau */}
      {activeBudget.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 w-8">N°</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Formateur</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-300">Matières</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-300">Copies</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300">Montant</th>
                  <th className="text-right px-3 py-3 font-semibold text-amber-600 dark:text-amber-400">Bonus</th>
                  <th className="text-right px-4 py-3 font-semibold text-green-700 dark:text-green-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {activeBudget.map((fb, idx) => (
                  <React.Fragment key={fb.enseignant_id}>
                    <tr
                      className={`border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}
                      onClick={() => toggleExpand(fb.enseignant_id)}
                    >
                      <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {expanded.has(fb.enseignant_id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{fb.nom}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">{fb.matieres.length}</td>
                      <td className="px-3 py-3 text-center font-semibold">{fb.total_copies}</td>
                      <td className="px-3 py-3 text-right">{fmtFCFA(fb.montant)}</td>
                      <td className="px-3 py-3 text-right text-amber-600 dark:text-amber-400">{fb.bonus > 0 ? fmtFCFA(fb.bonus) : '—'}</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700 dark:text-green-400">{fmtFCFA(fb.montant_total)}</td>
                    </tr>
                    {expanded.has(fb.enseignant_id) && (
                      <tr>
                        <td colSpan={7} className="px-8 pb-3 bg-blue-50/50 dark:bg-blue-900/10">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 py-2">
                            {fb.matieres.map((m, mi) => (
                              <div key={mi} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${m.copies < SEUIL_BONUS ? 'bg-amber-100 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`}>
                                <span className="font-medium">{m.nom} <span className="text-gray-400">({m.classes.join(', ')})</span></span>
                                <span className="ml-2 font-semibold">{m.copies} copies{m.copies < SEUIL_BONUS ? ' *' : ''}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                  <td colSpan={3} className="px-4 py-3 text-right">TOTAL</td>
                  <td className="px-3 py-3 text-center">{activeStats.totalCopies.toLocaleString('fr-FR')}</td>
                  <td className="px-3 py-3 text-right">{fmtFCFA(activeStats.totalMontant - activeStats.totalBonus)}</td>
                  <td className="px-3 py-3 text-right text-amber-600">{fmtFCFA(activeStats.totalBonus)}</td>
                  <td className="px-4 py-3 text-right text-green-700 dark:text-green-400 text-base">{fmtFCFA(activeStats.totalMontant)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            * Matières avec moins de {SEUIL_BONUS} copies : bonus de {fmtFCFA(BONUS_MONTANT)} ajouté par matière.
            Prix par copie : {PRIX_COPIE} FCFA.
          </div>
        </Card>
      )}
    </div>
  );
};

export default BudgetCorrection;
